import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Header from './Header';
import { hostUrl, apiUrl } from './config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faChartLine, faBuilding, faUsers, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import './Dashboard.css';

const Dashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalDevices: 0,
        activeDevices: 0,
        inactiveDevices: 0,
        totalOrganizations: 0,
        totalContacts: 0,
        recentLogs: [],
        topDevices: []
    });
    const [loading, setLoading] = useState(true);

    const getAccessToken = () => localStorage.getItem('access_token');

    const refreshAccessToken = async () => {
        try {
            const response = await axios.post(`${apiUrl}/token/refresh/`, {
                refresh: localStorage.getItem('refresh_token'),
            });
            localStorage.setItem('access_token', response.data.access);
            return response.data.access;
        } catch (error) {
            console.error('Failed to refresh access token', error);
            return null;
        }
    };

    const axiosInstance = axios.create();

    axiosInstance.interceptors.request.use(
        async (config) => {
            let token = getAccessToken();
            if (token) {
                const decodedToken = jwtDecode(token);
                const currentTime = Date.now() / 1000;

                if (decodedToken.exp < currentTime) {
                    token = await refreshAccessToken();
                }
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);

                // Fetch devices
                const devicesRes = await axiosInstance.get(`${hostUrl}/device/`);
                const devices = devicesRes.data.results || [];

                // Fetch organizations
                const orgsRes = await axiosInstance.get(`${hostUrl}/organization/`);
                const organizations = orgsRes.data.results || [];

                // Fetch contacts
                const contactsRes = await axiosInstance.get(`${hostUrl}/contact/`);
                const contacts = contactsRes.data.results || [];

                // Fetch recent logs
                const logsRes = await axiosInstance.get(`${hostUrl}/realtime_log/?limit=5&offset=0`);
                const logs = logsRes.data.results || [];

                // Calculate stats
                const activeDevices = devices.filter(d => d.is_active).length;
                const inactiveDevices = devices.length - activeDevices;

                setStats({
                    totalDevices: devices.length,
                    activeDevices: activeDevices,
                    inactiveDevices: inactiveDevices,
                    totalOrganizations: organizations.length,
                    totalContacts: contacts.length,
                    recentLogs: logs,
                    topDevices: devices.slice(0, 5)
                });
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const getErrorBadgeClass = (error) => {
        const errorMsg = String(error || '').toLowerCase();
        
        // Critical alerts - RED
        if (errorMsg && (errorMsg.includes('alert') || errorMsg.includes('error') || 
            errorMsg.includes('offline') || errorMsg.includes('power') || 
            errorMsg.includes('vacuum') || errorMsg.includes('critical'))) {
            return 'badge-alert-critical';
        }
        
        // Warning alerts - YELLOW
        if (errorMsg.includes('warning') || errorMsg.includes('battery') || 
            errorMsg.includes('low') || errorMsg.includes('high')) {
            return 'badge-alert-warning';
        }
        
        // Info alerts - BLUE
        if (errorMsg && errorMsg !== '' && !errorMsg.includes('ok') && !errorMsg.includes('normal')) {
            return 'badge-alert-info';
        }
        
        // Normal/OK - GREEN
        return 'badge-ok';
    };

    const getErrorBadgeText = (error) => {
        const errorMsg = String(error || '').toLowerCase();
        if (!errorMsg || errorMsg === '' || errorMsg === 'ok' || errorMsg === 'normal') {
            return 'Normal';
        }
        return error;
    };

    const formatTimestamp = (timestamp) => {
        /**
         * Convert timestamp to relative time format (e.g., "2 minutes ago")
         */
        if (!timestamp) return 'N/A';
        
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const seconds = Math.floor((now - date) / 1000);
            
            if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
            
            const minutes = Math.floor(seconds / 60);
            if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
            
            const hours = Math.floor(minutes / 60);
            if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
            
            const days = Math.floor(hours / 24);
            if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
            
            const months = Math.floor(days / 30);
            if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
            
            const years = Math.floor(months / 12);
            return `${years} year${years !== 1 ? 's' : ''} ago`;
        } catch (error) {
            return String(timestamp);
        }
    };

    const StatCard = ({ icon, label, value, color, onClick }) => (
        <div 
            className={`stat-card stat-card-${color}`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : 'default' }}
        >
            <div className="stat-icon">
                <FontAwesomeIcon icon={icon} />
            </div>
            <div className="stat-content">
                <p className="stat-label">{label}</p>
                <p className="stat-value">{value}</p>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
                <Header />
                <main style={{ flex: 1, marginTop: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            <Header />
            <main style={{
                flex: 1,
                marginTop: '70px',
                overflow: 'auto',
                padding: '30px',
                backgroundColor: '#f5f7fa'
            }}>
                <div className="dashboard-container">
                    {/* Header */}
                    <div className="dashboard-header">
                        <h1>Dashboard</h1>
                        <p>Welcome to IoT Device Management</p>
                    </div>

                    {/* Stats Grid */}
                    <div className="stats-grid">
                        <StatCard
                            icon={faServer}
                            label="Total Devices"
                            value={stats.totalDevices}
                            color="blue"
                            onClick={() => navigate('/device')}
                        />
                        <StatCard
                            icon={faCheck}
                            label="Active Devices"
                            value={stats.activeDevices}
                            color="green"
                            onClick={() => navigate('/device')}
                        />
                        <StatCard
                            icon={faTimes}
                            label="Inactive Devices"
                            value={stats.inactiveDevices}
                            color="red"
                            onClick={() => navigate('/device')}
                        />
                        <StatCard
                            icon={faBuilding}
                            label="Organizations"
                            value={stats.totalOrganizations}
                            color="purple"
                            onClick={() => navigate('/organization')}
                        />
                        <StatCard
                            icon={faUsers}
                            label="Total Contacts"
                            value={stats.totalContacts}
                            color="orange"
                            onClick={() => navigate('/contact')}
                        />
                        <StatCard
                            icon={faChartLine}
                            label="Recent Logs"
                            value={stats.recentLogs.length}
                            color="cyan"
                            onClick={() => navigate('/realtime_log')}
                        />
                    </div>

                    {/* Recent Logs Section */}
                    <div className="section">
                        <h2>Recent Logs</h2>
                        <div className="table-wrapper">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Device</th>
                                        <th>Vacuum</th>
                                        <th>Temp (°C)</th>
                                        <th>Error</th>
                                        <th>Timestamp</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.recentLogs.length > 0 ? (
                                        stats.recentLogs.map((log) => (
                                            <tr key={log.id}>
                                                <td>{log.id}</td>
                                                <td>{log.ssn || 'N/A'}</td>
                                                <td>{log.vacuum || 'N/A'}</td>
                                                <td>{log.vacu_temp || 'N/A'}</td>
                                                <td>
                                                    <span className={getErrorBadgeClass(log.error)}>
                                                        {getErrorBadgeText(log.error)}
                                                    </span>
                                                </td>
                                                <td>{formatTimestamp(log.ts)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'center', color: '#999' }}>
                                                No logs available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Top Devices Section */}
                    <div className="section">
                        <h2>Top Devices</h2>
                        <div className="devices-grid">
                            {stats.topDevices.length > 0 ? (
                                stats.topDevices.map((device) => (
                                    <div key={device.id} className="device-card">
                                        <div className="device-header">
                                            <h3>{device.name || `Device ${device.id}`}</h3>
                                            <span className={`status-badge status-${device.is_active ? 'active' : 'inactive'}`}>
                                                {device.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="device-details">
                                            <p><strong>Serial:</strong> {device.serial_number}</p>
                                            <p><strong>eSIM:</strong> {device.esim || 'N/A'}</p>
                                            <p><strong>IP:</strong> {device.ip_address || 'N/A'}</p>
                                            <p><strong>Last Update:</strong> {formatTimestamp(device.last_data_timestamp)}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#999' }}>
                                    No devices available
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
